/**
 * API クライアント
 *
 * フェッチ要求をラップして:
 * - 全リクエストに credentials: 'include' を自動追加
 * - 401/403 エラー時に自動トークンリフレッシュ
 * - エラーハンドリング（リフレッシュ失敗 → ログイン画面へリダイレクト）
 * - JSON リクエスト/レスポンス処理
 */

// 開発環境のみ localhost フォールバックを使用
// 本番/QA 環境では環境変数が設定されていない場合はエラーとなる
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? 'http://localhost:8080' : '');

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// リフレッシュ中かどうかを追跡するフラグ（重複リフレッシュを防止）
let isRefreshing = false;
// リフレッシュ完了を待つ Promise 配列
let refreshSubscribers: ((success: boolean) => void)[] = [];

/**
 * リフレッシュ完了を通知
 */
function onRefreshComplete(success: boolean) {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
}

/**
 * リフレッシュ完了を待つ
 */
function waitForRefresh(): Promise<boolean> {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });
}

/**
 * トークンをリフレッシュ
 * Cookie ベースなので body は不要
 */
async function refreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}

/**
 * 401/403 エラー時にトークンリフレッシュを試みる
 */
async function handleAuthError(): Promise<boolean> {
  // 既にリフレッシュ中の場合は完了を待つ
  if (isRefreshing) {
    return waitForRefresh();
  }

  isRefreshing = true;

  try {
    const success = await refreshToken();
    onRefreshComplete(success);
    return success;
  } finally {
    isRefreshing = false;
  }
}

/**
 * HTTP リクエストを実行（リトライ付き）
 */
async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  isRetry = false
): Promise<T> {
  const response = await fetch(url, options);

  if (response.ok) {
    return response.json() as Promise<T>;
  }

  // 401 または 403 で、まだリトライしていない場合はトークンリフレッシュを試みる
  if ((response.status === 401 || response.status === 403) && !isRetry) {
    const refreshSuccess = await handleAuthError();

    if (refreshSuccess) {
      // リフレッシュ成功 → 元のリクエストをリトライ
      return fetchWithRetry<T>(url, options, true);
    }

    // リフレッシュ失敗 → ログイン画面へリダイレクト
    window.location.href = '/';
    throw new ApiError(401, 'Session expired. Please log in again.');
  }

  // その他のエラー
  // バックエンドの ErrorResponse 形式をパースして、ユーザーフレンドリーなメッセージを抽出
  const errorText = await response.text();
  let errorMessage = response.statusText;

  if (errorText) {
    try {
      const errorJson = JSON.parse(errorText);
      // バックエンドの ErrorResponse 形式: { status, message, timestamp }
      errorMessage = errorJson.message || errorText;
    } catch {
      // JSON パースに失敗した場合はそのままテキストを使用
      errorMessage = errorText;
    }
  }

  throw new ApiError(response.status, errorMessage);
}

/**
 * ジェネリック GET リクエスト
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  return fetchWithRetry<T>(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

/**
 * ジェネリック POST リクエスト
 */
export async function apiPost<T, TBody = unknown>(
  endpoint: string,
  body?: TBody
): Promise<T> {
  return fetchWithRetry<T>(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * ジェネリック PUT リクエスト
 */
export async function apiPut<T, TBody = unknown>(
  endpoint: string,
  body: TBody
): Promise<T> {
  return fetchWithRetry<T>(`${API_BASE_URL}${endpoint}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

/**
 * ジェネリック DELETE リクエスト
 */
export async function apiDelete<T>(endpoint: string): Promise<T> {
  return fetchWithRetry<T>(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Axios 風のインターフェース（互換性のため）
 */
export const apiClient = {
  get: async <T>(endpoint: string) => {
    const data = await apiGet<T>(endpoint);
    return { data };
  },
  post: async <T, TBody = unknown>(endpoint: string, body?: TBody) => {
    const data = await apiPost<T, TBody>(endpoint, body);
    return { data };
  },
  put: async <T, TBody = unknown>(endpoint: string, body: TBody) => {
    const data = await apiPut<T, TBody>(endpoint, body);
    return { data };
  },
  delete: async <T>(endpoint: string) => {
    const data = await apiDelete<T>(endpoint);
    return { data };
  },
};
